import React, { useEffect, useRef, useState } from 'react'
import { useMidiMappable } from '@/hooks/useMidiMappable'
import { useMidiStore } from '@/stores/midiStore'
import { useAudioEngine } from '@/hooks/useAudioEngine'
import { useWebMidi } from '@/hooks/useWebMidi'
import { QuantizePopover } from '@/components/sequencer/QuantizePopover'
import { RecordButton } from '@/components/sequencer/RecordButton'
import { ArmRecordButton } from '@/components/sequencer/ArmRecordButton'

interface TransportProps {
  isPlaying: boolean
  bpm: number
  midiEnabled: boolean
  midiInEnabled: boolean
  onTogglePlay: () => void
  onChangeBpm: (bpm: number) => void
  onToggleMidi: () => void
  onToggleMidiIn: () => void
}

/**
 * Play/stop, BPM, MIDI-out toggle. BPM uses a local draft string so
 * the user can clear the field or type intermediate values on mobile —
 * the store's clamp only runs on commit (blur / Enter).
 *
 * Each control opts in to MIDI mapping via `useMidiMappable`. The
 * MIDI-IN button carries an extra caret that opens a popover for
 * "Forward MIDI globally" — the setting lets external MIDI drive
 * mapped controls from any page, not just the sequencer.
 */
export const Transport: React.FC<TransportProps> = ({
  isPlaying,
  bpm,
  midiEnabled,
  midiInEnabled,
  onTogglePlay,
  onChangeBpm,
  onToggleMidi,
  onToggleMidiIn,
}) => {
  const [bpmDraft, setBpmDraft] = useState(String(bpm))
  const [forwardingPopoverOpen, setForwardingPopoverOpen] = useState(false)
  const midiInWrapRef = useRef<HTMLDivElement | null>(null)

  const midiForwardingEnabled = useMidiStore(s => s.midiForwardingEnabled)
  const setMidiForwardingEnabled = useMidiStore(s => s.setMidiForwardingEnabled)
  const { releaseAllInstruments } = useAudioEngine()
  const { getSequencerRoutedOutputs, dispatchToHardwareOutput } = useWebMidi()

  /**
   * Full MIDI + audio panic. Kills every held instrument voice (the
   * engine's force-release path) AND sends CC 123 (All Notes Off) on
   * every MIDI channel to every routed hardware output, so stuck notes
   * on external synths + in-app instruments both clear.
   */
  const panic = () => {
    releaseAllInstruments()
    const outputs = getSequencerRoutedOutputs()
    for (const { id, available } of outputs) {
      if (!available) continue
      for (let ch = 0; ch < 16; ch++) {
        // CC 120 = All Sound Off (kill the tail, not just gate it).
        dispatchToHardwareOutput(id, [0xb0 | ch, 120, 0])
        // CC 123 = All Notes Off (note gates released cleanly).
        dispatchToHardwareOutput(id, [0xb0 | ch, 123, 0])
      }
    }
  }

  useEffect(() => {
    setBpmDraft(String(bpm))
  }, [bpm])

  // Close the forwarding popover on outside click. Separate from
  // useEffect-on-focusout so the popover closes via any pointer path
  // including clicks on other Transport controls.
  useEffect(() => {
    if (!forwardingPopoverOpen) return
    const onDown = (e: MouseEvent) => {
      if (!midiInWrapRef.current?.contains(e.target as Node)) {
        setForwardingPopoverOpen(false)
      }
    }
    window.addEventListener('mousedown', onDown)
    return () => window.removeEventListener('mousedown', onDown)
  }, [forwardingPopoverOpen])

  const commitBpm = () => {
    const parsed = Number(bpmDraft)
    if (Number.isFinite(parsed) && parsed > 0) {
      onChangeBpm(parsed)
    } else {
      setBpmDraft(String(bpm))
    }
  }

  // MIDI mapping registrations — stable ids, kind-appropriate handlers.
  const playMap = useMidiMappable<HTMLButtonElement>({
    id: 'transport.play',
    kind: 'trigger',
    label: 'Play / Stop',
    trigger: onTogglePlay,
  })
  const bpmMap = useMidiMappable<HTMLInputElement>({
    id: 'transport.bpm',
    kind: 'continuous',
    label: 'BPM',
    min: 40,
    max: 240,
    setValue: v => {
      if (typeof v === 'number' && Number.isFinite(v)) {
        const clamped = Math.max(40, Math.min(240, Math.round(v)))
        onChangeBpm(clamped)
      }
    },
  })
  const midiInMap = useMidiMappable<HTMLButtonElement>({
    id: 'transport.midiIn',
    kind: 'toggle',
    label: 'MIDI In',
    setValue: v => {
      const want = Boolean(v)
      if (want !== midiInEnabled) onToggleMidiIn()
    },
  })
  const midiOutMap = useMidiMappable<HTMLButtonElement>({
    id: 'transport.midiOut',
    kind: 'toggle',
    label: 'MIDI Out',
    setValue: v => {
      const want = Boolean(v)
      if (want !== midiEnabled) onToggleMidi()
    },
  })

  return (
    <div className="flex items-center gap-4 sm:gap-5 flex-wrap rounded-xl bg-surface-sunken border border-brd-subtle px-4 py-3">
      <button
        type="button"
        ref={playMap.ref}
        onClick={e => {
          if (playMap.armForLearn(e)) return
          onTogglePlay()
        }}
        aria-pressed={isPlaying}
        className={`w-12 h-12 rounded-full grid place-items-center text-xl font-bold transition-colors text-[color:var(--accent-ink)] ${
          isPlaying
            ? 'bg-[color:var(--accent-hover)] shadow-[0_0_20px_var(--accent-halo)]'
            : 'bg-accent hover:bg-[color:var(--accent-hover)]'
        }`}
        title={isPlaying ? 'Stop (Space)' : 'Play (Space)'}
      >
        {isPlaying ? '■' : '▶'}
      </button>

      <RecordButton />
      <ArmRecordButton />

      <label className="flex flex-col text-[10px] font-mono uppercase tracking-wider text-content-tertiary">
        BPM
        <input
          type="text"
          ref={bpmMap.ref}
          inputMode="numeric"
          pattern="[0-9]*"
          value={bpmDraft}
          onChange={e => setBpmDraft(e.target.value.replace(/[^\d]/g, ''))}
          onBlur={commitBpm}
          onMouseDown={e => {
            // Intercept clicks during Learn so selecting the input
            // arms BPM for MIDI capture instead of entering edit mode.
            if (bpmMap.armForLearn(e)) {
              e.currentTarget.blur()
            }
          }}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault()
              ;(e.target as HTMLInputElement).blur()
            }
          }}
          className="mt-1 w-20 bg-transparent text-accent text-lg font-semibold font-mono focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-halo)] rounded px-1"
        />
      </label>

      {/* MIDI-IN split button: main button toggles enable (unchanged
          behavior), caret opens the forwarding popover. The wrapper
          div is the ref the popover pins to for outside-click
          detection. */}
      <div ref={midiInWrapRef} className="relative flex items-stretch">
        <button
          type="button"
          ref={midiInMap.ref}
          onClick={e => {
            if (midiInMap.armForLearn(e)) return
            onToggleMidiIn()
          }}
          aria-pressed={midiInEnabled}
          className={`text-[10px] font-mono uppercase tracking-wider rounded-l px-2 py-1 transition-colors ${
            midiInEnabled
              ? 'text-second bg-[color:var(--second-soft)] border border-second'
              : 'text-content-tertiary border border-brd-subtle hover:border-brd-emphasis'
          }`}
          title={
            midiInEnabled
              ? 'MIDI in enabled — routed hardware inputs drive pads + mapped controls.'
              : 'MIDI in disabled — click to receive MIDI from routed hardware inputs.'
          }
        >
          MIDI IN
        </button>
        <button
          type="button"
          onClick={() => setForwardingPopoverOpen(open => !open)}
          aria-haspopup="menu"
          aria-expanded={forwardingPopoverOpen}
          className={`px-1.5 rounded-r border text-[10px] font-mono transition-colors flex items-center ${
            midiForwardingEnabled
              ? 'text-second bg-[color:var(--second-soft)] border-second border-l-0'
              : 'text-content-tertiary border-brd-subtle border-l-0 hover:border-brd-emphasis'
          }`}
          title="MIDI forwarding options"
        >
          ▾
        </button>
        {forwardingPopoverOpen && (
          <div
            role="menu"
            className="absolute top-full left-0 mt-1 z-50 w-72 rounded-lg border border-brd-subtle bg-surface-raised shadow-xl p-3 flex flex-col gap-2"
          >
            <label className="flex items-start gap-2 text-[11px] text-content-secondary cursor-pointer select-none">
              <input
                type="checkbox"
                checked={midiForwardingEnabled}
                onChange={e => setMidiForwardingEnabled(e.target.checked)}
                className="mt-0.5 accent-[color:var(--accent)]"
              />
              <span>
                <span className="font-semibold text-content-primary">
                  Forward MIDI globally
                </span>
                <span className="block text-[10px] text-content-tertiary mt-0.5">
                  Route incoming MIDI to mapped UI controls regardless of which
                  page is open.
                </span>
              </span>
            </label>
            <p className="text-[10px] text-content-tertiary">
              Map controls from the MIDI panel below (Learn + Clear). Hold{' '}
              <kbd className="font-mono text-[9px] px-1 py-0.5 rounded border border-brd-subtle bg-surface-sunken">
                Shift+M
              </kbd>{' '}
              to reveal current bindings.
            </p>
          </div>
        )}
      </div>

      <button
        type="button"
        ref={midiOutMap.ref}
        onClick={e => {
          if (midiOutMap.armForLearn(e)) return
          onToggleMidi()
        }}
        aria-pressed={midiEnabled}
        className={`text-[10px] font-mono uppercase tracking-wider rounded px-2 py-1 transition-colors ${
          midiEnabled
            ? 'text-second bg-[color:var(--second-soft)] border border-second'
            : 'text-content-tertiary border border-brd-subtle hover:border-brd-emphasis'
        }`}
        title={
          midiEnabled
            ? 'MIDI out enabled — sequencer sends note + CC events to routed hardware outputs.'
            : 'MIDI out disabled — click to send MIDI to routed hardware outputs.'
        }
      >
        MIDI OUT
      </button>

      <button
        type="button"
        onClick={panic}
        title="Panic — release every held voice locally AND send MIDI All-Notes-Off to every routed hardware output on channels 1-16."
        aria-label="Panic — stop all sound"
        className="text-[10px] font-mono uppercase tracking-wider rounded px-2 py-1 border border-brd-subtle text-content-secondary transition-colors hover:border-destructive hover:text-destructive hover:bg-destructive/10"
      >
        ✋ Panic
      </button>

      <QuantizePopover />
    </div>
  )
}
